import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { X, MapPin, Navigation } from 'lucide-react';

const GMAPS_LIBRARIES = ['places'];

const mapContainerStyle = {
    width: '100%',
    height: '280px',
    borderRadius: '10px'
};

const defaultCenter = { lat: 20.5937, lng: 78.9629 };

function getGoogleMapsApiKey() {
    try {
        const raw = localStorage.getItem('frontEndEnvData');
        if (raw) {
            const k = JSON.parse(raw)?.googleMapsApiKey;
            if (k) return k;
        }
    } catch (_) {
        /* ignore */
    }
    if (process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
        return process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    }
    return 'AIzaSyD65RKBAGVtP_QeI-cF-NnEjpN6GBtcOVg';
}

/**
 * Pick a point on the map (or use device GPS) to share as a WhatsApp location message.
 * Uses the same @react-google-maps/api + key pattern as GoogleMapComponent.
 */
export default function LeadShareLocationModal({ onClose, onConfirm }) {
    const googleMapsApiKey = getGoogleMapsApiKey();

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey,
        libraries: GMAPS_LIBRARIES
    });

    const mapRef = useRef(null);
    const searchWrapRef = useRef(null);

    const [pick, setPick] = useState(null);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    useEffect(() => {
        const onDocMouseDown = (e) => {
            if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
                setSearchResults([]);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, []);

    const performSearch = useCallback((query) => {
        const q = (query || '').trim();
        if (!q || !mapRef.current || !window.google?.maps) {
            setSearchResults([]);
            return;
        }
        const service = new window.google.maps.places.PlacesService(mapRef.current);
        const bounds = new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(8.0666, 68.1113),
            new window.google.maps.LatLng(37.1, 97.395)
        );
        service.textSearch({ query: q, bounds }, (results, status) => {
            if (
                status === window.google.maps.places.PlacesServiceStatus.OK &&
                results &&
                results.length > 0
            ) {
                setSearchResults(results.slice(0, 8));
            } else {
                setSearchResults([]);
            }
        });
    }, []);

    const handleSearchChange = (e) => {
        const v = e.target.value;
        setSearchQuery(v);
        performSearch(v);
    };

    const handleSelectSearchResult = (result) => {
        const lat = result.geometry.location.lat();
        const lng = result.geometry.location.lng();
        const next = { lat, lng };
        setPick(next);
        setMapCenter(next);
        setName(result.name || '');
        setAddress(result.formatted_address || '');
        setSearchQuery(result.formatted_address || result.name || '');
        setSearchResults([]);
        if (mapRef.current) {
            mapRef.current.panTo(next);
            mapRef.current.setZoom(16);
        }
    };

    const reverseGeocode = useCallback((lat, lng) => {
        if (!window.google?.maps) return;
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status !== 'OK' || !results?.[0]) return;
            const r = results[0];
            setAddress(r.formatted_address || '');
            const establishment = r.address_components?.find((c) =>
                c.types.includes('establishment')
            )?.long_name;
            const premise = r.address_components?.find((c) => c.types.includes('premise'))?.long_name;
            const poi = r.address_components?.find((c) =>
                c.types.includes('point_of_interest')
            )?.long_name;
            const label = establishment || premise || poi || r.formatted_address?.split(',')[0]?.trim();
            setName((prev) => (prev.trim() ? prev : label || ''));
        });
    }, []);

    const handleMapClick = (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        const next = { lat, lng };
        setPick(next);
        setMapCenter(next);
        setName('');
        reverseGeocode(lat, lng);
    };

    const handleMarkerDragEnd = (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        const next = { lat, lng };
        setPick(next);
        setMapCenter(next);
        setName('');
        reverseGeocode(lat, lng);
    };

    const useCurrentLocation = () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported in this browser.');
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                const next = { lat, lng };
                setPick(next);
                setMapCenter(next);
                setName('');
                reverseGeocode(lat, lng);
                if (mapRef.current) {
                    mapRef.current.panTo(next);
                    mapRef.current.setZoom(16);
                }
            },
            () => alert('Could not read your location. Allow location access and try again.'),
            { enableHighAccuracy: true, timeout: 15000 }
        );
    };

    const handleConfirm = () => {
        if (!pick) {
            alert('Choose a location on the map, search for a place, or use your current location.');
            return;
        }
        onConfirm({
            latitude: pick.lat,
            longitude: pick.lng,
            name: name.trim(),
            address: address.trim()
        });
    };

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div
            className="leaddetail-loc-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="leaddetail-loc-modal-title"
            onClick={onClose}
        >
            <div className="leaddetail-loc-modal" onClick={(e) => e.stopPropagation()}>
                <div className="leaddetail-loc-modal-header">
                    <h2 id="leaddetail-loc-modal-title" className="leaddetail-loc-modal-title">
                        <MapPin size={18} /> Share location
                    </h2>
                    <button
                        type="button"
                        className="leaddetail-loc-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                <p className="leaddetail-loc-modal-hint">
                    Use your current position, search, or tap the map to drop a pin. Drag the pin to
                    adjust.
                </p>

                <button
                    type="button"
                    className="leaddetail-loc-modal-current-btn"
                    onClick={useCurrentLocation}
                    disabled={!isLoaded}
                >
                    <Navigation size={16} /> Use current location
                </button>

                <div className="leaddetail-loc-modal-search-wrap" ref={searchWrapRef}>
                    <input
                        type="text"
                        className="leaddetail-loc-modal-search-input"
                        placeholder="Search for a place…"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        disabled={!isLoaded}
                    />
                    {searchResults.length > 0 && (
                        <ul className="leaddetail-loc-modal-results" role="listbox">
                            {searchResults.map((r, i) => (
                                <li key={r.place_id || i}>
                                    <button
                                        type="button"
                                        className="leaddetail-loc-modal-result"
                                        onClick={() => handleSelectSearchResult(r)}
                                    >
                                        <span className="leaddetail-loc-modal-result-name">
                                            {r.name || 'Place'}
                                        </span>
                                        <span className="leaddetail-loc-modal-result-addr">
                                            {r.formatted_address}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="leaddetail-loc-modal-map-wrap">
                    {loadError && (
                        <div className="leaddetail-loc-modal-map-fallback">
                            Could not load Google Maps. Check your API key and network.
                        </div>
                    )}
                    {!loadError && !isLoaded && (
                        <div className="leaddetail-loc-modal-map-fallback">Loading map…</div>
                    )}
                    {isLoaded && !loadError && (
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={mapCenter}
                            zoom={pick ? 16 : 5}
                            onClick={handleMapClick}
                            onLoad={(map) => {
                                mapRef.current = map;
                            }}
                            options={{
                                mapTypeControl: false,
                                streetViewControl: false,
                                fullscreenControl: false,
                                zoomControl: true
                            }}
                        >
                            {pick && (
                                <Marker
                                    position={pick}
                                    draggable
                                    onDragEnd={handleMarkerDragEnd}
                                />
                            )}
                        </GoogleMap>
                    )}
                </div>

                <div className="leaddetail-loc-modal-fields">
                    <input
                        type="text"
                        className="leaddetail-msg-input leaddetail-loc-modal-field"
                        placeholder="Place name (optional, shown in chat)"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <input
                        type="text"
                        className="leaddetail-msg-input leaddetail-loc-modal-field"
                        placeholder="Address (optional)"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                    />
                </div>

                <div className="leaddetail-loc-modal-actions">
                    <button type="button" className="leaddetail-cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="leaddetail-send-btn"
                        onClick={handleConfirm}
                        disabled={!pick}
                    >
                        <MapPin size={14} /> Add to message
                    </button>
                </div>
            </div>
        </div>
    );
}
